/* @flow */
import { root } from './conf'
import qs from 'query-string'
import http from 'axios'
import Store from 'lockr'

/**
 * Locale typename
 * @private
 * @memberof LocaleService
 */
const typename = 'locale'

/**
 * Gets all locales except manatee. Saves them to local storage.
 *
 * @function
 * @memberof LocaleService
 *
 * @return { Promise<Object> } Locale names and their keys.
 */
const getLocales = (): Promise<Object> => {
  const params = {
    typename: typename
  }

  return http.get(`${root}/oftype?${qs.stringify(params)}`).then(res => {
    let localeKeys = []
    let locales = []

    if (res && res.data) {
      let filteredLocales = res.data.filter(
        l => l.name && !l.name.toLowerCase().startsWith('manatee')
      )
      localeKeys = (filteredLocales || []).map(l => l.name)
      locales = filteredLocales || []
      setCachedLocales(locales)
    }
    return { localeKeys, locales }
  })
}

/**
 * Gets all MANATEE locales.
 *
 * @function
 * @memberof LocaleService
 *
 * @return { Promise<Object> } Locale names and their keys.
 */
const getManateeLocales = (): Promise<Object> => {
  const params = {
    typename: typename
  }

  return http.get(`${root}/oftype?${qs.stringify(params)}`).then(res => {
    let localeKeys = []
    let locales = []

    if (res && res.data) {
      let filteredLocales = res.data.filter(
        l => l.name && l.name.toLowerCase().startsWith('manatee')
      )
      localeKeys = (filteredLocales || []).map(l => l.name)
      locales = filteredLocales || []
    }
    return { localeKeys, locales }
  })
}

/**
 * Gets all cached locales.
 * @function
 * @memberof LocaleService
 *
 * @return { Object } Cached locales.
 */
const getCachedLocales = (): Object => Store.get('locale') || {}

const setCachedLocales = (locales: Array<any>): void => {
  const localeByKeys = locales.reduce((acc, cur) => {
    acc[cur.name] = cur.descriptive
    return acc
  }, {})
  Store.set('locale', localeByKeys)
}

/**
 * Gets cached language key.
 * @function
 * @memberof LocaleService
 *
 * @return { string } Cached lang.
 */
const getCachedLangKey = (): string => Store.get('currentLang') || ''

/**
 * Updates cached language key.
 * @function
 * @memberof LocaleService
 *
 * @return { undefined }
 */
const updateCachedLang = (lang: string): any => Store.set('currentLang', lang)

/**
 * Remove locale by id.
 */
const removeLocale = localeId => {
  const data = {
    typename: 'locale',
    identifier: localeId
  }

  return http.post(`${root}/withdrawbyid`, data)
}

/**
 * Drops current translations for lang & writes new ones.
 */
const addLocalesForLang = (lang: string, locales: any) => {
  const params = {
    typename: 'locale',
    name: lang
  }

  return http
    .get(`${root}/byname?${qs.stringify(params)}`)
    .catch(err => {
      console.error(err)
      return { data: [] }
    })
    .then(res => {
      const promises = res.data
        ? res.data.map(locale => removeLocale(locale.id))
        : [new Promise(resolve => resolve())]

      return Promise.all(promises)
    })
    .then(() => http.post(`${root}/provide`, locales))
    .then(() => console.info(`✅  Locales saved for ${lang}`))
    .catch(err => console.error(`❌  Failed to save locales for ${lang}`, err))
}

const addLocales = () => {
  const locales = require('../locales/locales')
  return Promise.all(locales.map(l => addLocalesForLang(l.item.name, l)))
}

const self = {
  getLocales,
  getManateeLocales,
  getCachedLocales,
  getCachedLangKey,
  addLocalesForLang,
  updateCachedLang,
  addLocales,
  setCachedLocales
}

/**
 * Locale service: Get & cache locales.
 * @module LocaleService
 * @namespace LocaleService
 * @version 1.0.0
 */
export default self
